/**
 * マイルストーンサービス
 *
 * milestone_templates と project_milestones テーブルへのアクセスを提供
 */

import { supabase } from '../lib/supabase'
import { isDemoModeActive } from '../contexts/DemoContext'
import { getDemoMilestonesByProjectId, getDemoMilestonesByProjectIds, getDemoMilestoneTemplates, addDemoMilestone, updateDemoMilestone, deleteDemoMilestone } from '../mocks/demoData'

// 型定義
export interface MilestoneTemplate {
  id: string
  team_id: string | null  // チーム用テンプレートの場合
  user_id?: string | null // 個人用テンプレートの場合
  title: string
  icon_type: 'emoji' | 'lucide'
  icon_value: string
  color: string
  sort_order: number
  created_at: string
  updated_at: string
}

export interface ProjectMilestone {
  id: string
  project_id: string
  template_id: string | null
  title: string
  start_date: string  // YYYY-MM-DD
  end_date: string | null
  icon_type: 'emoji' | 'lucide'
  icon_value: string
  color: string
  sort_order: number
  created_at: string
  updated_at: string
}

// 作成用の型（id, created_at, updated_at は自動生成）
export type CreateMilestoneTemplate = Omit<MilestoneTemplate, 'id' | 'created_at' | 'updated_at'>
export type CreateProjectMilestone = Omit<ProjectMilestone, 'id' | 'created_at' | 'updated_at'>

// デフォルトマイルストーンテンプレート（チーム/個人用の初期テンプレート）
export const DEFAULT_MILESTONE_TEMPLATES: Omit<CreateMilestoneTemplate, 'team_id' | 'user_id'>[] = [
  {
    title: '企画開始',
    icon_type: 'emoji',
    icon_value: '💡',
    color: '#3B82F6',
    sort_order: 0,
  },
  {
    title: '撮影開始',
    icon_type: 'emoji',
    icon_value: '🎬',
    color: '#22C55E',
    sort_order: 1,
  },
  {
    title: '編集開始',
    icon_type: 'emoji',
    icon_value: '✂️',
    color: '#F59E0B',
    sort_order: 2,
  },
  {
    title: '公開日',
    icon_type: 'emoji',
    icon_value: '🎉',
    color: '#EF4444',
    sort_order: 3,
  },
  {
    title: '締め切り',
    icon_type: 'emoji',
    icon_value: '⏰',
    color: '#8B5CF6',
    sort_order: 4,
  },
]

// 更新用の型
export type UpdateMilestoneTemplate = Partial<Omit<MilestoneTemplate, 'id' | 'team_id' | 'created_at' | 'updated_at'>>
export type UpdateProjectMilestone = Partial<Omit<ProjectMilestone, 'id' | 'project_id' | 'created_at' | 'updated_at'>>

// =====================================================
// マイルストーンテンプレート
// =====================================================

/**
 * チームのマイルストーンテンプレートを取得
 */
export async function getMilestoneTemplatesByTeamId(teamId: string): Promise<MilestoneTemplate[]> {
  // デモモード中はデモデータを返す
  if (isDemoModeActive()) {
    return getDemoMilestoneTemplates() as MilestoneTemplate[]
  }

  const { data, error } = await supabase
    .from('milestone_templates')
    .select('*')
    .eq('team_id', teamId)
    .order('sort_order', { ascending: true })

  if (error) {
    console.error('Error fetching milestone templates:', error)
    throw error
  }

  return data || []
}

/**
 * マイルストーンテンプレートを作成
 */
export async function createMilestoneTemplate(template: CreateMilestoneTemplate): Promise<MilestoneTemplate> {
  // デモモード中はDB呼び出しをスキップ
  if (isDemoModeActive()) {
    // ダミーのテンプレートを返す
    return {
      id: `demo-template-${Date.now()}`,
      team_id: template.team_id,
      user_id: template.user_id,
      title: template.title,
      icon_type: template.icon_type,
      icon_value: template.icon_value,
      color: template.color,
      sort_order: template.sort_order,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
  }

  const { data, error } = await supabase
    .from('milestone_templates')
    .insert(template)
    .select()
    .single()

  if (error) {
    console.error('Error creating milestone template:', error)
    throw error
  }

  return data
}

/**
 * マイルストーンテンプレートを更新
 */
export async function updateMilestoneTemplate(id: string, updates: UpdateMilestoneTemplate): Promise<MilestoneTemplate> {
  const { data, error } = await supabase
    .from('milestone_templates')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('Error updating milestone template:', error)
    throw error
  }

  return data
}

/**
 * マイルストーンテンプレートを削除
 */
export async function deleteMilestoneTemplate(id: string): Promise<void> {
  const { error } = await supabase
    .from('milestone_templates')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting milestone template:', error)
    throw error
  }
}

// =====================================================
// 個人用マイルストーンテンプレート
// =====================================================

/**
 * ユーザーの個人用マイルストーンテンプレートを取得
 */
export async function getMilestoneTemplatesByUserId(userId: string): Promise<MilestoneTemplate[]> {
  const { data, error } = await supabase
    .from('milestone_templates')
    .select('*')
    .eq('user_id', userId)
    .is('team_id', null)
    .order('sort_order', { ascending: true })

  if (error) {
    console.error('Error fetching personal milestone templates:', error)
    throw error
  }

  return data || []
}

/**
 * 個人用マイルストーンテンプレートを作成
 */
export async function createPersonalMilestoneTemplate(
  userId: string,
  template: Omit<CreateMilestoneTemplate, 'team_id' | 'user_id'>
): Promise<MilestoneTemplate> {
  const { data, error } = await supabase
    .from('milestone_templates')
    .insert({
      ...template,
      user_id: userId,
      team_id: null,
    })
    .select()
    .single()

  if (error) {
    console.error('Error creating personal milestone template:', error)
    throw error
  }

  return data
}

/**
 * ユーザーにデフォルトテンプレートがなければ初期化
 * @returns 作成されたテンプレート（既存があれば空配列）
 */
export async function initializePersonalTemplatesIfNeeded(userId: string): Promise<MilestoneTemplate[]> {
  // 既存のテンプレートを確認
  const existing = await getMilestoneTemplatesByUserId(userId)
  if (existing.length > 0) {
    return [] // 既にテンプレートがある場合は何もしない
  }

  // デフォルトテンプレートを作成
  const created: MilestoneTemplate[] = []
  for (const template of DEFAULT_MILESTONE_TEMPLATES) {
    const result = await createPersonalMilestoneTemplate(userId, template)
    created.push(result)
  }

  return created
}

/**
 * チームにデフォルトテンプレートがなければ初期化
 * @returns 作成されたテンプレート（既存があれば空配列）
 */
export async function initializeTeamTemplatesIfNeeded(teamId: string): Promise<MilestoneTemplate[]> {
  // デモモード中はスキップ
  if (isDemoModeActive()) {
    return []
  }

  // 既存のテンプレートを確認
  const existing = await getMilestoneTemplatesByTeamId(teamId)
  if (existing.length > 0) {
    return [] // 既にテンプレートがある場合は何もしない
  }

  // デフォルトテンプレートを作成
  const created: MilestoneTemplate[] = []
  for (const template of DEFAULT_MILESTONE_TEMPLATES) {
    const result = await createMilestoneTemplate({
      ...template,
      team_id: teamId,
    })
    created.push(result)
  }

  return created
}

// =====================================================
// プロジェクトマイルストーン
// =====================================================

/**
 * プロジェクトのマイルストーンを取得
 */
export async function getMilestonesByProjectId(projectId: string): Promise<ProjectMilestone[]> {
  // デモモード中はデモデータを返す
  if (isDemoModeActive()) {
    return getDemoMilestonesByProjectId(projectId) as ProjectMilestone[]
  }

  const { data, error } = await supabase
    .from('project_milestones')
    .select('*')
    .eq('project_id', projectId)
    .order('start_date', { ascending: true })

  if (error) {
    console.error('Error fetching project milestones:', error)
    throw error
  }

  return data || []
}

/**
 * 複数プロジェクトのマイルストーンを一括取得
 */
export async function getMilestonesByProjectIds(projectIds: string[]): Promise<ProjectMilestone[]> {
  if (projectIds.length === 0) return []

  // デモモード中はデモデータを返す
  if (isDemoModeActive()) {
    return getDemoMilestonesByProjectIds(projectIds) as ProjectMilestone[]
  }

  const { data, error } = await supabase
    .from('project_milestones')
    .select('*')
    .in('project_id', projectIds)
    .order('start_date', { ascending: true })

  if (error) {
    console.error('Error fetching project milestones:', error)
    throw error
  }

  return data || []
}

/**
 * プロジェクトマイルストーンを作成
 */
export async function createProjectMilestone(milestone: CreateProjectMilestone): Promise<ProjectMilestone> {
  // デモモード中はデモデータに追加
  if (isDemoModeActive()) {
    return addDemoMilestone(milestone) as ProjectMilestone
  }

  const { data, error } = await supabase
    .from('project_milestones')
    .insert(milestone)
    .select()
    .single()

  if (error) {
    console.error('Error creating project milestone:', error)
    throw error
  }

  return data
}

/**
 * プロジェクトマイルストーンを更新
 */
export async function updateProjectMilestone(id: string, updates: UpdateProjectMilestone): Promise<ProjectMilestone> {
  // デモモード中はデモデータを更新
  if (isDemoModeActive()) {
    return updateDemoMilestone(id, updates) as ProjectMilestone
  }

  const { data, error } = await supabase
    .from('project_milestones')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('Error updating project milestone:', error)
    throw error
  }

  return data
}

/**
 * プロジェクトマイルストーンを削除
 */
export async function deleteProjectMilestone(id: string): Promise<void> {
  // デモモード中はデモデータから削除
  if (isDemoModeActive()) {
    deleteDemoMilestone(id)
    return
  }

  const { error } = await supabase
    .from('project_milestones')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting project milestone:', error)
    throw error
  }
}

/**
 * テンプレートからマイルストーンを作成
 */
export async function createMilestoneFromTemplate(
  template: MilestoneTemplate,
  projectId: string,
  startDate: string,
  endDate?: string
): Promise<ProjectMilestone> {
  return createProjectMilestone({
    project_id: projectId,
    template_id: template.id,
    title: template.title,
    start_date: startDate,
    end_date: endDate || null,
    icon_type: template.icon_type,
    icon_value: template.icon_value,
    color: template.color,
    sort_order: 0,  // 後で調整可能
  })
}
